import React from 'react';

/**
 * GroupHela Platform Logo Component
 *
 * Reusable logo with brand name and tagline
 * Features: Interlocking circles (unity), diamond (value), coins (finance), connecting lines (network)
 *
 * @param {Object} props
 * @param {boolean} props.showText - Show brand name and tagline (default: true)
 * @param {boolean} props.showTagline - Show tagline (default: true)
 * @param {'xs'|'sm'|'md'|'lg'|'xl'} props.size - Logo size preset
 * @param {string} props.className - Additional CSS classes
 */
const Logo = ({ showText = true, showTagline = true, size = 'md', className = '' }) => {
  const sizeClasses = {
    xs: { icon: 'w-8 h-8', text: 'text-lg', tagline: 'text-[0.5rem]', p: 'p-1.5' },
    sm: { icon: 'w-10 h-10', text: 'text-2xl', tagline: 'text-[0.6rem]', p: 'p-2' },
    md: { icon: 'w-14 h-14', text: 'text-4xl', tagline: 'text-xs', p: 'p-3' },
    lg: { icon: 'w-20 h-20', text: 'text-5xl', tagline: 'text-sm', p: 'p-4' },
    xl: { icon: 'w-24 h-24', text: 'text-6xl', tagline: 'text-base', p: 'p-5' },
  };

  const sizes = sizeClasses[size] || sizeClasses.md;

  return (
    <div className={`flex items-center justify-center gap-4 ${className}`}>
      {/* Logo Icon */}
      <div className="relative flex-shrink-0">
        {/* Glow Effect */}
        <div className="absolute inset-0 bg-gradient-to-r from-emerald-500 via-cyan-500 to-amber-500 rounded-2xl blur-xl opacity-20 animate-pulse" />

        {/* Main Logo */}
        <div
          className={`relative bg-gradient-to-br from-[#1a1f2e] to-[#0f1115] rounded-2xl ${sizes.p} border border-white/10 backdrop-blur-sm`}
        >
          <svg
            className={sizes.icon}
            viewBox="0 0 120 120"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            {/* Circular Background Accent */}
            <circle cx="60" cy="60" r="55" fill="url(#logoGradient)" opacity="0.1" />

            {/* Interlocking Circles - Representing Unity & Collaboration */}
            <circle
              cx="45"
              cy="55"
              r="18"
              stroke="url(#logoGradient)"
              strokeWidth="3.5"
              fill="none"
              opacity="0.8"
            />
            <circle
              cx="75"
              cy="55"
              r="18"
              stroke="url(#logoGradient)"
              strokeWidth="3.5"
              fill="none"
              opacity="0.8"
            />

            {/* Center Diamond - Representing Value & Trust */}
            <path d="M60 40 L70 55 L60 70 L50 55 Z" fill="url(#logoGradient)" opacity="0.9" />

            {/* Coins Stack - Financial Symbol */}
            <ellipse cx="60" cy="75" rx="15" ry="4" fill="url(#logoGradient)" opacity="0.6" />
            <ellipse cx="60" cy="72" rx="15" ry="4" fill="url(#logoGradient)" opacity="0.7" />
            <ellipse cx="60" cy="69" rx="15" ry="4" fill="url(#logoGradient)" opacity="0.8" />

            {/* Connecting Lines - Community Network */}
            <path
              d="M45 73 Q60 80 75 73"
              stroke="url(#logoGradient)"
              strokeWidth="2"
              opacity="0.5"
              strokeLinecap="round"
            />

            {/* Gradient Definition */}
            <defs>
              <linearGradient id="logoGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#10b981" />
                <stop offset="50%" stopColor="#06b6d4" />
                <stop offset="100%" stopColor="#fbbf24" />
              </linearGradient>
            </defs>
          </svg>
        </div>
      </div>

      {/* Brand Name */}
      {showText && (
        <div className="text-left">
          <h1 className={`${sizes.text} font-bold tracking-tight`}>
            <span className="bg-gradient-to-r from-emerald-400 via-cyan-400 to-amber-300 bg-clip-text text-transparent">
              Group
            </span>
            <span className="text-white ml-2">Hela</span>
          </h1>
          {showTagline && (
            <p
              className={`text-emerald-400/60 ${sizes.tagline} font-medium tracking-widest uppercase mt-1`}
            >
              Financial Unity Platform
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default Logo;
